import { useNavigate } from "react-router-dom";
import { useRef, useEffect } from "react";
import { Plus, Radio, Send, ChevronLeft, Users, PenLine } from "lucide-react";
import { GroupAvatar } from "./BroadcastAtoms";
import LiveBroadcast from "./LiveBroadcast";
import ContactsPanel from "./ContactsPanel";
import { WA_BACKGROUND } from "./constants";

const ChatPanel = ({ selectedGroup, groupBroadcasts, showContacts, onToggleContacts, onBack, onNewBroadcast, mobileView }) => {
  const navigate   = useNavigate();
  const scrollAreaRef = useRef(null);

  // Scroll to bottom whenever broadcasts load or a new one is added
  useEffect(() => {
    const el = scrollAreaRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [groupBroadcasts]);

  if (!selectedGroup) {
    return (
      <div className={`flex-1 flex flex-col bg-[#f0f2f5] min-w-0 ${mobileView === "list" ? "hidden md:flex" : "flex"}`} style={{ height: "100%" }}>
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <div className="w-24 h-24 rounded-full bg-white shadow-sm flex items-center justify-center mb-4">
            <Radio className="w-10 h-10 text-gray-300" />
          </div>
          <h2 className="text-lg font-semibold text-gray-600 mb-1">Select a Group</h2>
          <p className="text-sm text-gray-400 max-w-xs">
            Click a group from the list to view its broadcast history and send new messages.
          </p>
          <button
            onClick={onNewBroadcast}
            className="mt-5 flex items-center gap-2 px-5 py-2.5 bg-[#075e54] text-white text-sm font-medium rounded-full hover:bg-[#064c44] transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Broadcast
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 flex flex-col bg-[#f0f2f5] min-w-0 ${mobileView === "list" ? "hidden md:flex" : "flex"}`} style={{ height: "100%" }}>

      {/* Chat header */}
      <div className="bg-[#075e54] text-white px-4 py-2.5 flex items-center gap-3 flex-shrink-0 shadow-md z-10">
        <button onClick={onBack} className="md:hidden p-1.5 rounded-full hover:bg-white/20 transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <GroupAvatar group={selectedGroup} size="lg" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{selectedGroup.name}</p>
          <p className="text-[11px] text-white/70">{selectedGroup.activeCustomerCount ?? 0} active contacts</p>
        </div>
        <button
          onClick={() => onToggleContacts(!showContacts)}
          title={showContacts ? "Close contacts" : "View contacts"}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors flex-shrink-0 ${
            showContacts
              ? "bg-white text-[#075e54]"
              : "bg-white/20 hover:bg-white/30 text-white"
          }`}
        >
          <Users className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Contacts</span>
          {(selectedGroup?.activeCustomerCount ?? 0) > 0 && (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none ${showContacts ? "bg-[#075e54] text-white" : "bg-white/30 text-white"}`}>
              {selectedGroup.activeCustomerCount > 99 ? "99+" : selectedGroup.activeCustomerCount}
            </span>
          )}
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-1 min-h-0 relative overflow-hidden">

        {/* Messages */}
        <div className="flex-1 flex flex-col min-w-0">
          <div ref={scrollAreaRef} className="flex-1 overflow-y-auto py-4 px-2" style={WA_BACKGROUND}>
            {groupBroadcasts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <div className="w-16 h-16 rounded-full bg-white/80 shadow-sm flex items-center justify-center mb-3">
                  <Send className="w-7 h-7 text-gray-300" />
                </div>
                <p className="text-sm font-medium text-gray-500">No broadcasts yet</p>
                <p className="text-xs text-gray-400 mt-1 mb-4">
                  Send your first message to <strong>{selectedGroup.name}</strong>
                </p>
                <button
                  onClick={onNewBroadcast}
                  className="flex items-center gap-2 px-4 py-2 bg-[#075e54] text-white text-sm font-medium rounded-full hover:bg-[#064c44] transition-colors shadow-md"
                >
                  <Plus className="w-4 h-4" />
                  Send First Broadcast
                </button>
              </div>
            ) : (
              // Render oldest first (API returns DESC) so newest appears at the bottom like WhatsApp
              [...groupBroadcasts].reverse().map((b) => <LiveBroadcast key={b.id} b={b} />)
            )}
          </div>

          {/* Bottom bar */}
          <div className="bg-[#f0f2f5] border-t border-gray-200 px-3 py-2.5 flex-shrink-0 flex items-center gap-2">
            <button
              onClick={onNewBroadcast}
              className="flex-1 flex items-center justify-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2.5 hover:bg-blue-50 hover:border-blue-200 transition-colors shadow-sm group"
            >
              <Send className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" />
              <span className="text-sm text-gray-500 group-hover:text-blue-600 font-medium select-none">Send Template</span>
            </button>
            <button
              onClick={() => navigate("/template/create")}
              className="flex-1 flex items-center justify-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2.5 hover:bg-green-50 hover:border-green-200 transition-colors shadow-sm group"
            >
              <PenLine className="w-4 h-4 text-gray-400 group-hover:text-green-600 transition-colors flex-shrink-0" />
              <span className="text-sm text-gray-500 group-hover:text-green-700 font-medium select-none">New Template</span>
            </button>
          </div>
        </div>

        {/* Contacts panel */}
        {showContacts && (
          <ContactsPanel group={selectedGroup} onClose={() => onToggleContacts(false)} />
        )}


      </div>
    </div>
  );
};

export default ChatPanel;
